import React from 'react';
import { schema, normalize } from 'normalizr';
import _ from 'lodash';
import { FormattedMessage } from 'react-intl';
import {
  ADD_SCHOOLS,
  SCHOOLS_API_REQUEST,
  SCHOOLS_API_SUCCESS,
  SCHOOLS_API_FAILURE,
  ADD_SCHOOL,
  SCHOOL_API_REQUEST,
  SCHOOL_API_SUCCESS,
  SCHOOL_API_FAILURE,
  ADD_SCHOOLS_PROGRAMS,
  SCHOOLS_PROGRAMS_API_REQUEST,
  SCHOOLS_PROGRAMS_API_SUCCESS,
  SCHOOLS_PROGRAMS_API_FAILURE,
  ADD_SCHOOLS_APPLICATIONS,
  SCHOOLS_APPLICATIONS_API_REQUEST,
  SCHOOLS_APPLICATIONS_API_SUCCESS,
  SCHOOLS_APPLICATIONS_API_FAILURE,
  UPDATE_WAITLIST_OFFERS,
  UPDATE_WAITLIST_OFFERS_SUCCESS,
  UPDATE_WAITLIST_OFFERS_FAILURE,
  WAITLIST_SUBMISSIONS_STATE,
  WAITLIST_SUBMISSIONS_STATE_SUCCESS,
  WAITLIST_SUBMISSIONS_STATE_FAILURE,
  SCHOOL_EVENTS_REQUEST_API,
  SCHOOL_EVENTS_SUCCESS_API,
  SCHOOL_EVENTS_FAILURE_API,
} from './actionTypes';
import { setLastPage, unsetLastPage } from './pagination';
import messages from '../locale/messages';
import fetchErrorHandler from '../services/fetchErrorHandler';
import SubwayIcon from '../components/icons/Subway';
import AccessibilityIcon from '../components/icons/Accessibility';
import MortarboardIcon from '../components/icons/Mortarboard';
import CardCurrentSchoolIcon from '../components/icons/CardCurrentSchool';
import { getCsrfToken, processNameToShortCode, hideGuaranteedOfferExcludedSchools } from '../utils/helpers';

import { SYNC_ADDED_PROGRAM_SAVED_PROGRAM } from '../constants';

const studentAppDataToSchools = (application, queryParams) => ({
  type: SYNC_ADDED_PROGRAM_SAVED_PROGRAM,
  payload: {
    application,
    queryParams,
  },
});

const schoolsApiRequest = () => ({
  type: SCHOOLS_API_REQUEST,
});

const schoolsApiSuccess = () => ({
  type: SCHOOLS_API_SUCCESS,
});

const schoolsApiFailure = ({ error }) => ({
  type: SCHOOLS_API_FAILURE,
  error,
});

const addSchools = ({ schools, page, normal, count, switches, zonedSchoolLoginRequiredText, requestId }) => ({
  type: ADD_SCHOOLS,
  payload: {
    schools,
    page,
    normal,
    count,
    switches,
    zonedSchoolLoginRequiredText,
    requestId
  },
});

const schoolApiRequest = () => ({
  type: SCHOOL_API_REQUEST,
});

const schoolApiSuccess = () => ({
  type: SCHOOL_API_SUCCESS,
});

const schoolApiFailure = ({ error }) => ({
  type: SCHOOL_API_FAILURE,
  error,
});

const addSchool = ({ school, page, normal }) => ({
  type: ADD_SCHOOL,
  payload: {
    school,
    page,
    normal,
  },
});

const schoolsApplicationsApiRequest = () => ({
  type: SCHOOLS_APPLICATIONS_API_REQUEST,
});

const schoolsApplicationsApiSuccess = () => ({
  type: SCHOOLS_APPLICATIONS_API_SUCCESS,
});

const schoolsApplicationsApiFailure = ({ error }) => ({
  type: SCHOOLS_APPLICATIONS_API_FAILURE,
  error,
});

const addSchoolsApplications = ({ schools, page, normal }) => ({
  type: ADD_SCHOOLS_APPLICATIONS,
  payload: {
    schools,
    page,
    normal,
  },
});

const schoolsProgramsApiRequest = () => ({
  type: SCHOOLS_PROGRAMS_API_REQUEST,
});

const schoolsProgramsApiSuccess = () => ({
  type: SCHOOLS_PROGRAMS_API_SUCCESS,
});

const schoolsProgramsApiFailure = ({ error }) => ({
  type: SCHOOLS_PROGRAMS_API_FAILURE,
  error,
});

const addSchoolsPrograms = ({ schools, page, normal }) => ({
  type: ADD_SCHOOLS_PROGRAMS,
  payload: {
    schools,
    page,
    normal,
  },
});

const indSchool = new schema.Entity(
  'school',
  {},
  {
    processStrategy: school => ({
      details: false,
      ...school,
    }),
  },
);
export const normalSchools = new schema.Array(indSchool);

const apiToPrograms = program => {
  const tipIcon = '';
  const tip = '';
  let eligible = null;
  let studentPriorityGroup = null;
  const testing_not_registered = null;
  const demandLastYear = program.demand_last_year;
  const generalEducation = demandLastYear.general_education;
  const studentsWithDisabilities = demandLastYear.students_with_disabilities;
  const hours = program.start_time && program.end_time ? `${program.start_time} - ${program.end_time}` : null;
  const applicantsPerSeat = studentsWithDisabilities.is_swd
    ? studentsWithDisabilities.applications_per_seat
    : generalEducation.applications_per_seat; // mys-943
  if (program.directory_student_program) {
    if (program.directory_student_program[0]) {
      eligible = program.directory_student_program[0].eligible;
      studentPriorityGroup = program.directory_student_program[0].priority_group;
    }
  }
  return {
    ...program,
    title: program.name,
    tipIcon,
    tip,
    url: 'yahoo.com',
    hours,
    id: program.id,
    savedApplication: !!program.application_program_id,
    savedApplicationId: program.application_program_id,
    testing_not_registered: program.testing_not_registered,
    saved: !!program.saved_program_id,
    savedProgramId: program.saved_program_id ? program.saved_program_id : false,
    joinedWaitlist: !!program.waitlist_program_id,
    waitlistProgramId: program.waitlist_program_id ? program.waitlist_program_id : false,
    onSubmittedApplication: program.on_submitted_application,
    description: program.description,
    admissionMethod: program.admissions_method,
    scheduling: program.scheduling,
    eligible,
    getPriorityGroups() {
      if (studentPriorityGroup) {
        return {
          all: program.directory_priority_groups,
          forStudent: studentPriorityGroup.order,
          // sk: intentional change as per Sifter
          // https://blenderbox.sifterapp.com/issues/34223
          // studentPriorityGroupDescription: studentPriorityGroup.description,
          // "Then to " is removed for the tooltip only!
          studentPriorityGroupDescription: studentPriorityGroup.name,
        };
      }
      return {
        all: program.program_priority_groups, // program.directory_priority_groups,
        forStudent: null,
        studentPriorityGroupDescription: '',
      };
    },
    applicantsPerSeat,
    allProgramJson: {
      ...program,
    },
  };
};

export const apiToSchools = school => {
  const { address } = school.school;
  const { name, accessibility } = school;
  const gradeRange = school.grades_description;
  const startTime = school.start_time;
  const endTime = school.end_time;
  const hours = startTime && endTime ? `${startTime} - ${endTime}` : null;
  const {
    activities,
    id,
    overview,
    bus,
    meals,
    subway,
    email,
    telephone,
    independent_website: website,
    uniform,
    magnet,
    bap_url,
  } = school;
  const sharedSpace = school.shared_space;
  const { address_1: streetAddress, city, state, zip_code: zipCode } = address;
  const programList = school.programs.map(apiToPrograms);
  const indProgram = new schema.Entity('program');
  const normalPrograms = new schema.Array(indProgram);
  const normalProgs = normalize(programList, normalPrograms);
  const schoolUrl = `/schools/${school.school.dbn}/`;
  const perfLabels = school.performance_labels || {};
  return {
    ...school,
    id,
    getProgram(programId) {
      return this.programs.entities.program[programId];
    },
    getPrograms() {
      this.programs.result.map(programId => this.programs.entities.program[programId]);
    },
    getSavedApplications() {
      return _.without(
        this.programs.result.map(programId => {
          if (this.programs.entities.program && this.programs.entities.program[programId].savedApplication) {
            return this.programs.entities.program[programId];
          }
          return undefined;
        }),
        undefined,
      );
    },
    getSavedPrograms() {
      return _.without(
        this.programs.result.map(programId => {
          if (this.programs.entities.program[programId].saved) {
            return this.programs.entities.program[programId];
          }
          return undefined;
        }),
        undefined,
      );
    },
    getStats(loggedIn, admissionProcess) {
      const stats = [];
      const parts = window.location.pathname.split('/');
      const processName = parts.pop() || parts.pop(); // handle potential trailing slash
      const loggedOutAdmissionProcess = processNameToShortCode({ processName });
      if (loggedIn && school.is_current_school && !hideGuaranteedOfferExcludedSchools(school.school.dbn)) {
        let tip = <FormattedMessage {...messages.schoolProgramCurrentSchoolIconMessage} />;
        if (admissionProcess === 'K') {
          tip = <FormattedMessage {...messages.schoolProgramCurrentSchoolIconMessageK} />;
        }

        stats.push({
          key: `stat-${school.id}-current-school`,
          icon: <CardCurrentSchoolIcon size={20} fill="#024cbc" />,
          label: '',
          tip,
          ariaLabel: messages.schoolProgramCurrentSchoolIconMessage,
          ariaLabelMessageData: {},
        });
      }
      if (school.distance) {
        stats.push({
          key: `stat-${school.id}-distance`,
          icon: <SubwayIcon size={18} />,
          label: school.distance,
          tip: '',
          ariaLabel: messages.generalStringDistanceFromHome,
          ariaLabelMessageData: {},
        });
      }
      stats.push({
        key: `stat-${school.id}-accessibility`,
        icon: <AccessibilityIcon size={31} />,
        label: '',
        tip: accessibility,
        ariaLabel: messages.generalStringAccessibilityInfo,
        ariaLabelMessageData: { schoolName: school.name },
      });
      if (
        !(
          admissionProcess === 'PK' ||
          admissionProcess === '2K' ||
          admissionProcess === '3K' ||
          loggedOutAdmissionProcess === 'PK' ||
          loggedOutAdmissionProcess === '2K' ||
          loggedOutAdmissionProcess === '3K'
        ) &&
        school.grades_description
      ) {
        stats.push({
          key: `stat-${school.id}-grade-range`,
          icon: <MortarboardIcon size={18} />,
          label: gradeRange,
          tip: '',
          ariaLabel: messages.generalStringGradeRange,
          ariaLabelMessageData: {},
        });
      }
      return stats;
    },
    title: name,
    latlong: {
      lat: address ? parseFloat(address.latitude) : 0,
      lng: address ? parseFloat(address.longitude) : 0,
    },
    icon: 'default',
    overview,
    accessibility,
    isAffiliatedSRLocation: school.is_affiliated_sr_location,
    affiliatedSRLocationText: school.affiliated_sr_location_text,
    programs: normalProgs,
    info: {
      id,
      diversityInAdmissions: school.diversity_in_admissions,
      contactName: school.contact_name,
      admissionsOpen: school.admissions_open,
      waitlistOpen: school.waitlist_open,
      resultsEnabled: school.results_enabled,
      otherFeatures: school.other_features,
      affiliatedSchools: school.affiliated_schools,
      address: `${streetAddress}, ${city}, ${state}, ${zipCode}`,
      website,
      secondary_website: school.secondary_website,
      email,
      phone: telephone,
      grades: gradeRange,
      subway,
      bus,
      studentCount: school.total_enrollment,
      hours,
      sections: [
        { title: 'Fit for student' },
        { title: 'Performance' },
        { title: 'Academics' },
        { title: 'Activities' },
        { title: 'More Info' },
      ],
      performance: {
        graduation: school.stat_graduation,
        enrollCollegeCareer: school.stat_enroll_college_career,
        attendance: school.stat_attendance,
        saftey: school.stat_safety,
        stat_safety_hall_caf: school.stat_safety_hall_caf,
        stat_safety_bath_lock: school.stat_safety_bath_lock,
        variety: school.stat_variety,
        stateCoreCourses: school.stat_core_courses,
        statStateEla: school.stat_state_ela,
        statStateMath: school.stat_state_math,
        frequentlyAttendedHS: school.frequently_attended_hs,
        statParentsSatisfaction: school.stat_parents_satisfaction,
        instructionAndPerformanceRating: school.instruction_and_performance_rating
          ? school.instruction_and_performance_rating
          : false,
        safetyAndSchoolClimateRating: school.safety_and_school_climate_rating
          ? school.safety_and_school_climate_rating
          : false,
        relationshipsWithFamiliesRating: school.relationships_with_families_rating
          ? school.relationships_with_families_rating
          : false,
        instructionAndPerformanceLabel: perfLabels.instruction_and_performance_label
          ? perfLabels.instruction_and_performance_label
          : false,
        safetyAndSchoolClimateLabel: perfLabels.safety_and_school_climate_label
          ? perfLabels.safety_and_school_climate_label
          : false,
        relationshipsWithFamiliesLabel: perfLabels.relationships_with_families_label
          ? perfLabels.relationships_with_families_label
          : false,
        school_quality_snapshot_html: perfLabels.school_quality_snapshot_html
          ? perfLabels.school_quality_snapshot_html
          : false,
      },
      academics: {
        opportunities: school.academic_opportunities,
        ell: school.ell_programs,
        languageClasses: school.language_classes,
        apCourses: school.advanced_placement_courses,
        virtualLearning: school.virtual_learning,
        diplomaEndorsements: school.diploma_endorsements,
        electivesDescription: school.electives_description,
        artCourses: school.art_courses,
      },
      activities: {
        activities,
        activitiesDescription: school.activities_description,
        sportsBoys: school.sports_boys,
        sportsGirls: school.sports_girls,
        sportsCoed: school.sports_coed,
        sportsSchool: school.school_sports,
        sportsDescription: school.sports_description,
      },
      accessibility,
      openHouses: school.open_house_information,
      meals,
      uniform,
      sharedSpace,
      magnet,
      switches: school.switches,
      entireJsonSchool: {
        ...school,
      },
      bapurl: bap_url,
    },
    accessible: !!(id % 2),
    url: schoolUrl,
    enrollmentRange: school.enrollment_range,
    scrolled: false,
    distance: school.distance,
  };
};
const apiToProgramsCards = program => ({
  ...program,
  saved: !!program.saved_program_id,
  savedProgramId: program.saved_program_id ? program.saved_program_id : false,
  joinedWaitlist: !!program.waitlist_program_id,
  waitlistProgramId: program.waitlist_program_id ? program.waitlist_program_id : false,
});

const apiToSchoolsCards = school => {
  const programList = school.programs.map(apiToProgramsCards);
  const indProgram = new schema.Entity('program');
  const normalPrograms = new schema.Array(indProgram);
  const normalProgs = normalize(programList, normalPrograms);
  const schoolUrl = `/schools/${school.id}`;

  return {
    ...school,
    programs: normalProgs,
    getProgram(programId) {
      return this.programs.entities.program[programId];
    },
    url: schoolUrl,
  };
};

export const schoolHover = id => dispatch => {
  dispatch({ type: 'SCHOOL_HOVER', payload: id });
};

export const getSchools = ({
  //
  extra = '',
  isAutoComplete = false,
  latLng = '',
  processName = 'high-school',
  mapBounds = '',
}) => async (
  //
  dispatch,
  getState,
  { fetch },
) => {
  const { apiVersion } = getState().runtime;
  const { page } = getState().page;
  const { optionsDict } = getState().schools;
  const { locale } = getState().intl;
  const processId = getState().admissionInstances[processName];
  let { applicationId, studentAffiliatedSchool, isSR } = getState().user;
  let url = `${locale}/api/${apiVersion}/application/${applicationId}/schools${extra}`;
  if (applicationId === '') {
    applicationId = false;
    url = `${locale}/api/${apiVersion}/schools/process/${processId}${extra}`;
  }
  let parametersUri = [];

  // sk: skip all the filtering if we are clicking an autocomplete
  // we want this school no matter what!
  if (!isAutoComplete) {
    Object.entries(optionsDict).forEach(([key, value]) => {
      if (value.length > 0) {
        return value.map(e => parametersUri.push(`${encodeURIComponent(key)}=${encodeURIComponent(e)}`));
      }
      return false;
    });
    parametersUri = parametersUri.map(x => x).join('&');
  }
  const pageUrl = `page=${page}&`;

  dispatch(schoolsApiRequest());
  const { latestSchoolsRequestId: requestId } = getState().schools;

  return fetch(`${url}/?${pageUrl}${mapBounds}${latLng}${parametersUri}`, {
    credentials: 'include',
    headers: {
      'content-type': 'application/x-www-form-urlencoded; charset=UTF-8',
      'X-CSRFToken': getCsrfToken(),
    },
  })
    .then(fetchErrorHandler)
    .then(resp => resp.json())
    .then(data => {
      let schools = '';
      let count = 1;
      if (!data.results) {
        schools = [];
        schools.push(data);
        schools = schools.map(apiToSchools);
      } else {
        schools = data.results.map(apiToSchools);
        if (data.next === null) {
          dispatch(setLastPage());
        } else {
          dispatch(unsetLastPage());
        }
        count = data.count;
      }

      const { switches } = data;
      const { zoned_school_login_required_text: zonedSchoolLoginRequiredText = '' } = data;
      const normal = normalize(schools, normalSchools);
      if (isSR && normal.entities.school) {
        const affiliatedSchool = Object.values(normal.entities.school).find(
          entry => entry.name === studentAffiliatedSchool,
        );
        if (affiliatedSchool) {
          affiliatedSchool.is_affiliated_sr_location = true;
          affiliatedSchool.isAffiliatedSRLocation = true;
        }
      }

      dispatch(
        addSchools({
          schools,
          page,
          normal,
          count,
          switches,
          zonedSchoolLoginRequiredText,
          requestId: requestId
        }),
      );
      dispatch(schoolsApiSuccess());
      if (applicationId) {
        const queryParams = `${pageUrl}${mapBounds}${latLng}${parametersUri}`;
        dispatch(studentAppDataToSchools(applicationId, queryParams));
      }
    })
    .catch(error => {
      console.error('error', error);
      dispatch(schoolsApiFailure({ error }));
    });
};

export const getSchoolsPrograms = (extra = '') => async (dispatch, getState, { fetch }) => {
  const { apiVersion } = getState().runtime;
  const { optionsDict } = getState().schools;
  const { locale } = getState().intl;
  const { applicationId, studentAffiliatedSchool, isSR } = getState().user;
  const url = `${locale}/api/${apiVersion}/application/${applicationId}/saved-programs/schools${extra}`;
  let parametersUri = [];
  Object.entries(optionsDict).forEach(([key, value]) => {
    if (value.length > 0) {
      return value.map(e => parametersUri.push(`${encodeURIComponent(key)}=${encodeURIComponent(e)}`));
    }
    return false;
  });
  parametersUri = parametersUri.map(x => x).join('&');
  dispatch(schoolsProgramsApiRequest());

  return fetch(`${url}?${parametersUri}`, {
    credentials: 'include',
    headers: {
      'content-type': 'application/x-www-form-urlencoded; charset=UTF-8',
      'X-CSRFToken': getCsrfToken(),
    },
  })
    .then(fetchErrorHandler)
    .then(resp => resp.json())
    .then(data => {
      let schools = '';
      if (!data.results) {
        // sk: no more pagination
        schools = data.map(apiToSchools);
      } else {
        schools = data.results.map(apiToSchools);
      }
      const normal = normalize(schools, normalSchools);
      if (isSR && normal.entities.school) {
        const affiliatedSchool = Object.values(normal.entities.school).find(
          entry => entry.name === studentAffiliatedSchool,
        );
        if (affiliatedSchool) {
          affiliatedSchool.is_affiliated_sr_location = true;
          affiliatedSchool.isAffiliatedSRLocation = true;
        }
      }

      dispatch(
        addSchoolsPrograms({
          schools,
          normal,
        }),
      );
      dispatch(schoolsProgramsApiSuccess());
    })
    .catch(error => {
      console.error('error', error);
      dispatch(schoolsProgramsApiFailure({ error }));
    });
};

export const getSavedProgramCards = () => async (dispatch, getState, { fetch }) => {
  const { apiVersion } = getState().runtime;
  const { locale } = getState().intl;
  const { applicationId } = getState().user;
  const url = `${locale}/api/${apiVersion}/application/${applicationId}/saved-program-cards/`;
  dispatch(schoolsProgramsApiRequest());

  return fetch(url, {
    credentials: 'include',
    headers: {
      'content-type': 'application/x-www-form-urlencoded; charset=UTF-8',
      'X-CSRFToken': getCsrfToken(),
    },
  })
    .then(fetchErrorHandler)
    .then(resp => resp.json())
    .then(data => {
      let schools = '';
      if (!data.results) {
        // sk: no more pagination
        schools = data.map(apiToSchoolsCards);
      } else {
        schools = data.results.map(apiToSchoolsCards);
      }
      const normal = normalize(schools, normalSchools);
      dispatch(
        addSchoolsPrograms({
          schools,
          normal,
        }),
      );
      dispatch(schoolsProgramsApiSuccess());
    })
    .catch(error => {
      console.error('error', error);
      dispatch(schoolsProgramsApiFailure({ error }));
    });
};

export const getSchoolsApplications = (extra = '') => async (dispatch, getState, { fetch }) => {
  const { apiVersion } = getState().runtime;
  const { optionsDict } = getState().schools;
  const { locale } = getState().intl;
  const { applicationId, studentAffiliatedSchool, isSR } = getState().user;
  const url = `${locale}/api/${apiVersion}/application/${applicationId}/saved-programs-apply/schools${extra}`;
  let parametersUri = [];
  Object.entries(optionsDict).forEach(([key, value]) => {
    if (value.length > 0) {
      return value.map(e => parametersUri.push(`${encodeURIComponent(key)}=${encodeURIComponent(e)}`));
    }
    return false;
  });
  parametersUri = parametersUri.map(x => x).join('&');
  dispatch(schoolsApplicationsApiRequest());

  return fetch(`${url}${parametersUri}`, {
    credentials: 'include',
    headers: {
      'content-type': 'application/x-www-form-urlencoded; charset=UTF-8',
      'X-CSRFToken': getCsrfToken(),
    },
  })
    .then(fetchErrorHandler)
    .then(resp => resp.json())
    .then(data => {
      let schools = '';
      if (!data.results) {
        schools = [];
        schools.push(data);
        schools = schools.map(apiToSchools);
      } else {
        schools = data.results.map(apiToSchools);
      }
      const normal = normalize(schools, normalSchools);
      if (isSR && normal.entities.school) {
        const affiliatedSchool = Object.values(normal.entities.school).find(
          entry => entry.name === studentAffiliatedSchool,
        );
        if (affiliatedSchool) {
          affiliatedSchool.is_affiliated_sr_location = true;
          affiliatedSchool.isAffiliatedSRLocation = true;
        }
      }

      dispatch(
        addSchoolsApplications({
          schools,
          normal,
        }),
      );
      dispatch(schoolsApplicationsApiSuccess());
    })
    .catch(error => {
      console.error('error', error);
      dispatch(schoolsApplicationsApiFailure({ error }));
    });
};

export const getSchool = id => async (dispatch, getState, { fetch }) => {
  const { apiVersion } = getState().runtime;
  const { locale } = getState().intl;
  const { applicationId, studentAffiliatedSchool, isSR } = getState().user;
  const url = `${locale}/api/${apiVersion}/application/${applicationId}/schools/${id}`;
  dispatch(schoolApiRequest());

  return fetch(`${url}`, {
    credentials: 'include',
    headers: {
      'content-type': 'application/x-www-form-urlencoded; charset=UTF-8',
      'X-CSRFToken': getCsrfToken(),
    },
  })
    .then(fetchErrorHandler)
    .then(resp => resp.json())
    .then(data => {
      let school = '';
      if (!data.results) {
        school = [];
        school.push(data);
        school = school.map(apiToSchools);
      } else {
        school = data.results.map(apiToSchools);
      }
      const normal = normalize(school, normalSchools);
      if (isSR && normal.entities.school) {
        const affiliatedSchool = Object.values(normal.entities.school).find(
          entry => entry.name === studentAffiliatedSchool,
        );
        if (affiliatedSchool) {
          affiliatedSchool.is_affiliated_sr_location = true;
          affiliatedSchool.isAffiliatedSRLocation = true;
        }
      }

      dispatch(
        addSchool({
          school,
          normal,
        }),
      );

      dispatch(schoolApiSuccess());
    })
    .catch(error => {
      console.error('error', error);
      dispatch(schoolApiFailure({ error }));
    });
};

export const handleOfferDecision = (id, waitlistId, accepted, extra = '') => async (dispatch, getState, { fetch }) => {
  const { apiVersion } = getState().runtime;
  const { locale } = getState().intl;
  const { applicationId } = getState().user;
  const url = `${locale}/api/${apiVersion}/application/${applicationId}/waitlist-programs/${waitlistId}/`;
  // eslint-disable-next-line camelcase
  const offer_response = accepted ? 'AC' : 'DE';
  const bodyData = { offer_status: 'OF', offer_response };

  return fetch(url, {
    credentials: 'include',
    headers: {
      'X-CSRFToken': getCsrfToken(),
    },
    method: 'PUT',
    body: JSON.stringify(bodyData),
  })
    .then(fetchErrorHandler)
    .then(resp => resp.json())
    .then(() => {
      dispatch({
        type: UPDATE_WAITLIST_OFFERS,
        payload: {
          waitlistId,
          offerResponse: offer_response,
        },
      });
      dispatch({ type: UPDATE_WAITLIST_OFFERS_SUCCESS });
    })
    .catch(error => {
      console.error('error', error);
      dispatch({ type: UPDATE_WAITLIST_OFFERS_FAILURE, payload: error });
    });
};

export const getWaitlistsSubmissionsState = () => async (dispatch, getState, { fetch }) => {
  const { apiVersion } = getState().runtime;
  const { locale } = getState().intl;
  const { applicationId } = getState().user;
  const url = `${locale}/api/${apiVersion}/application/${applicationId}/waitlist-submission-state/`;

  return fetch(url, {
    credentials: 'include',
    headers: {
      'content-type': 'application/x-www-form-urlencoded; charset=UTF-8',
      'X-CSRFToken': getCsrfToken(),
    },
  })
    .then(fetchErrorHandler)
    .then(resp => resp.json())
    .then(data => {
      dispatch({
        type: WAITLIST_SUBMISSIONS_STATE,
        payload: {
          ...data,
        },
      });
      dispatch({ type: WAITLIST_SUBMISSIONS_STATE_SUCCESS });
    })
    .catch(error => {
      console.error('error', error);
      dispatch({ type: WAITLIST_SUBMISSIONS_STATE_FAILURE, payload: error });
    });
};

/* ACTIONS FUNCTION FOR GETTING SCHOOLS EVENTS */
const schoolEventsApiRequest = () => ({
  type: SCHOOL_EVENTS_REQUEST_API,
});

const schoolEventsApiSuccess = ({ events }) => ({
  type: SCHOOL_EVENTS_SUCCESS_API,
  payload: {
    events,
  },
});

const schoolEventsApiFailure = ({ error }) => ({
  type: SCHOOL_EVENTS_FAILURE_API,
  error,
});

export const getSchoolEvents = (dbn, start, end) => async (
  //
  dispatch,
  getState,
  { fetch },
) => {
  const { locale } = getState().intl;
  const url = `${locale}/calendar/api/occurrences/?calling_source=g&school=${dbn}&start=${start}&end=${end}`;

  dispatch(schoolEventsApiRequest());

  return fetch(`${url}`)
    .then(fetchErrorHandler)
    .then(resp => resp.json())
    .then(events => {
      dispatch(schoolEventsApiSuccess({ events }));
      return events;
    })
    .catch(error => {
      dispatch(schoolEventsApiFailure({ error }));
    });
};
